/**
 * Coerces a string value (e.g. from clipboard paste) into the appropriate
 * CustomFieldValueUnion type based on the custom field's value type.
 * Returns null when the string cannot be parsed for the target type.
 */ import { Temporal } from "temporal-polyfill-lite";
import { CustomFieldValueType } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { DateWithOptionalTime } from "../common/DateWithOptionalTime";
function isDateWithOptionalTime(value) {
    return typeof value === "object" && value !== null && "date" in value && value.date instanceof Temporal.PlainDate;
}
/**
 * Coerce a raw value into the correct CustomFieldValueUnion for a given value type.
 * When the raw value is a string, it is parsed according to the target type:
 * - Number: parseFloat, returns null if NaN
 * - Flag: "true"→true, "false"→false, else null
 * - Date: Temporal.PlainDate.from wrapped in DateWithOptionalTime, returns null on failure
 * - Text / SingleChoiceText: passed through as-is
 *
 * Non-string values are returned unchanged.
 */ export function coerceCustomFieldStringValue(newValue, valueType) {
    if (typeof newValue !== "string") {
        if (newValue == null || typeof newValue === "number" || typeof newValue === "boolean" || isDateWithOptionalTime(newValue)) {
            return newValue;
        }
        return null; // Reject unexpected types (objects, arrays, etc.)
    }
    const rawValue = newValue;
    switch(valueType){
        case CustomFieldValueType.Number:
            {
                const parsed = parseFloat(rawValue);
                return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
            }
        case CustomFieldValueType.Flag:
            return rawValue === "true" ? true : rawValue === "false" ? false : null;
        case CustomFieldValueType.Date:
            try {
                return rawValue ? DateWithOptionalTime.fromPlainDate(Temporal.PlainDate.from(rawValue)) : null;
            } catch  {
                return null;
            }
        case CustomFieldValueType.Text:
        case CustomFieldValueType.SingleChoiceText:
        default:
            return rawValue;
    }
}
/**
 * Convert a typed CustomFieldValueUnion into the string form stored on ICustomFieldValue.value.
 * Encoding by valueType:
 * - Text / SingleChoiceText: the value itself (already a string)
 * - Number: the number's string representation (e.g. "42", "-3.14")
 * - Flag: "true" or "false"
 * - Date: ISO date string (e.g. "2025-12-25")
 */ export function customFieldValueToStorageString(value) {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number") {
        return value.toString();
    }
    if (typeof value === "boolean") {
        return value ? "true" : "false";
    }
    return value.date.toString();
}
/**
 * Convert a stored CustomFieldValue.value string back into the typed CustomFieldValueUnion form.
 * Returns null when the string cannot be parsed for the target type.
 */ export function customFieldValueFromStorageString(value, valueType) {
    switch(valueType){
        case CustomFieldValueType.Number:
            {
                const parsed = parseFloat(value);
                return !isNaN(parsed) && isFinite(parsed) ? parsed : null;
            }
        case CustomFieldValueType.Flag:
            return value === "true";
        case CustomFieldValueType.Date:
            try {
                return value ? DateWithOptionalTime.fromPlainDate(Temporal.PlainDate.from(value)) : null;
            } catch  {
                return null;
            }
        case CustomFieldValueType.Text:
        case CustomFieldValueType.SingleChoiceText:
        default:
            return value;
    }
}
